/**
 * Refine data provider over our REST API.
 *
 * Hand-written rather than `@refinedev/simple-rest`, because the API is not shaped
 * to that convention and should not be: simple-rest expects `_start`/`_end` range
 * params and a total in an `x-total-count` header. Ours takes `page`/`pageSize` and
 * returns `{ data, total }`, which is a better API for everything that is not Refine
 * — the portal, the job runner, curl. The adapter belongs on this side.
 *
 * The provider is deliberately thin. It maps Refine's vocabulary onto ours and does
 * nothing else: no caching, no optimistic updates, no client-side filtering. Refine
 * owns the first two and the third is a correctness trap — a client filtering rows
 * it already received is a client that has already received rows it should not have.
 * Scoping happens in Postgres.
 */

import type { DataProvider } from '@refinedev/core';
import { ApiError, api } from './api.js';

/** Our list envelope. */
interface ListEnvelope<T> {
  data: T[];
  total: number;
}

/**
 * Resources whose update verb is PATCH with a partial body.
 *
 * All of them, currently. Named explicitly rather than assumed because the
 * sensitive sub-resources are PUT with a full body — see `writeSensitive` below,
 * which is not part of the Refine surface at all.
 */
function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

/**
 * Translate Refine's filters into our query parameters.
 *
 * Only the filters our endpoints actually implement are passed through. An unknown
 * filter is DROPPED rather than forwarded, and that is the safe direction: forwarding
 * `?ssn=...` to an endpoint that ignores it would silently return unfiltered rows
 * while the UI claimed a filter was applied.
 */
const SUPPORTED_FILTERS = new Set([
  'search',
  'propertyId',
  'unitId',
  'leaseId',
  'tenantId',
  'ownerId',
  'status',
  'availableForLease',
  'documentType',
  'includeArchived',
  'chargeType',
  'includeVoided',
  'asOf',
  'minDaysLate',
  'vendorId',
  'priority',
  'workOrderId',
  'withinDays',
  'from',
  'to',
  'applicationId',
]);

export const dataProvider: DataProvider = {
  getApiUrl: () => '/api',

  async getList({ resource, pagination, filters }) {
    // `current`, not `currentPage` — Refine v4's Pagination field name. v5 renamed
    // it, and the wrong one silently reads as undefined and pins every list to
    // page 1, which looks like a broken pager rather than a typo.
    const page = pagination?.current ?? 1;
    const pageSize = pagination?.pageSize ?? 25;

    const query: Record<string, unknown> = { page, pageSize };

    for (const filter of filters ?? []) {
      // Logical filters (and/or groups) have no `field`. Our endpoints take flat
      // query parameters, so a grouped filter cannot be expressed and is skipped
      // rather than half-applied.
      if (!('field' in filter)) continue;
      if (!SUPPORTED_FILTERS.has(filter.field)) continue;
      query[filter.field] = filter.value;
    }

    const response = await api<ListEnvelope<Record<string, unknown>>>(
      `/${resource}${buildQuery(query)}`,
    );

    return { data: response.data as never, total: response.total };
  },

  async getOne({ resource, id }) {
    const record = await api<Record<string, unknown>>(`/${resource}/${String(id)}`);
    return { data: record as never };
  },

  async create({ resource, variables }) {
    const record = await api<Record<string, unknown>>(`/${resource}`, {
      method: 'POST',
      body: JSON.stringify(variables),
    });
    return { data: record as never };
  },

  async update({ resource, id, variables }) {
    const record = await api<Record<string, unknown>>(`/${resource}/${String(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(variables),
    });
    return { data: record as never };
  },

  /**
   * DELETE, which for properties, units, owners and tenants ARCHIVES rather than
   * removing (see the services — the row has to stay resolvable for charges,
   * payments and audit entries). The API returns 204 with no body, so there is no
   * record to hand back; Refine only uses the return value to update its cache and
   * tolerates an empty one.
   */
  async deleteOne({ resource, id }) {
    await api<void>(`/${resource}/${String(id)}`, { method: 'DELETE' });
    return { data: { id } as never };
  },

  // getMany, createMany, updateMany, deleteMany and custom are deliberately absent.
  //
  // They are optional in Refine's DataProvider, and Refine falls back to issuing
  // individual requests when a bulk method is missing. Implementing them as a loop
  // here would turn one user action into N requests with no transaction around them,
  // so a partial failure would leave half a bulk archive applied and nothing to roll
  // back. If bulk editing is wanted it needs an endpoint that does it in one
  // transaction, with one audit entry.
};

/**
 * The sensitive sub-resources, outside the Refine surface.
 *
 * Deliberately not modelled as Refine resources (invariant 5). A Refine resource
 * gets list screens, caching, and a `useTable` that would hold decrypted SSNs in a
 * client-side query cache — which is exactly the shape of accident the separate
 * table exists to prevent. These are explicit one-shot calls, and every one is
 * audited server-side as a READ.
 */
export async function readSensitive<T>(resource: 'tenants' | 'owners', id: string): Promise<T> {
  return api<T>(`/${resource}/${id}/sensitive`);
}

export async function writeSensitive<T>(
  resource: 'tenants' | 'owners',
  id: string,
  body: unknown,
): Promise<T> {
  return api<T>(`/${resource}/${id}/sensitive`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Upload a file: mint a presigned PUT, send the bytes straight to storage, then
 * confirm.
 *
 * The bytes never touch our API — see lib/storage.ts on the server. The PUT must
 * send exactly the content type and length that were signed, which is why they are
 * read off the File rather than passed in separately: a mismatch is rejected by
 * storage with an opaque 403.
 */
export interface UploadedDocument {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string | null;
}

export async function uploadDocument(
  file: File,
  meta: {
    propertyId?: string;
    unitId?: string;
    leaseId?: string;
    tenantId?: string;
    workOrderId?: string;
    ownerId?: string;
    applicationId?: string;
    documentType: string;
    visibility?: string;
    description?: string | null;
  },
): Promise<UploadedDocument> {
  const started = await api<{ document: { id: string }; uploadUrl: string }>('/documents/uploads', {
    method: 'POST',
    body: JSON.stringify({
      ...meta,
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    }),
  });

  // Raw fetch, not the api() wrapper: this goes to object storage, not to us. It
  // must not carry our session cookie — `credentials: 'omit'` — because an
  // unexpected Authorization or Cookie header changes the request S3 sees and
  // invalidates the signature.
  const put = await fetch(started.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type },
    credentials: 'omit',
  });

  if (!put.ok) {
    throw new ApiError(
      put.status,
      'UPLOAD_FAILED',
      `Storage rejected the upload (${String(put.status)}). ` +
        'The presigned URL may have expired — try again.',
    );
  }

  // Finalize confirms the object landed by HEADing it server-side. Until this
  // returns, the document row is unfinalized and invisible in listings.
  return api<UploadedDocument>(`/documents/${started.document.id}/finalize`, { method: 'POST' });
}

/**
 * The shared two-step mechanics behind a listing's featured-photo and
 * gallery-photo uploads: presign a PUT against `startPath`, send the bytes,
 * then hit `confirmPath` with the storage key. Same presigned-PUT shape as
 * `uploadDocument` above, minus its "unfinalized row" bookkeeping step — a
 * Listing (or ListingPhoto) row is only ever written once the object is
 * confirmed to exist, so there's nothing to clean up on a failed upload.
 */
async function uploadListingImage<T>(
  startPath: string,
  confirmPath: string,
  file: File,
): Promise<T> {
  const started = await api<{ storageKey: string; uploadUrl: string }>(startPath, {
    method: 'POST',
    body: JSON.stringify({ contentType: file.type, sizeBytes: file.size }),
  });

  const put = await fetch(started.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type },
    credentials: 'omit',
  });

  if (!put.ok) {
    throw new ApiError(
      put.status,
      'UPLOAD_FAILED',
      `Storage rejected the upload (${String(put.status)}). ` +
        'The presigned URL may have expired — try again.',
    );
  }

  return api<T>(confirmPath, {
    method: 'POST',
    body: JSON.stringify({ storageKey: started.storageKey }),
  });
}

/** The featured photo (step 11) — one per listing, replaces whatever was there. */
export async function uploadListingPhoto(
  listingId: string,
  file: File,
): Promise<{ photoUrl: string | null }> {
  return uploadListingImage(
    `/listings/${listingId}/photo`,
    `/listings/${listingId}/photo/confirm`,
    file,
  );
}

/** A gallery photo — appended, not replaced; see ListingPhoto's doc comment in schema.prisma. */
export async function uploadGalleryPhoto(
  listingId: string,
  file: File,
): Promise<{ id: string; photoUrl: string }> {
  return uploadListingImage(
    `/listings/${listingId}/photos`,
    `/listings/${listingId}/photos/confirm`,
    file,
  );
}
